/**
 * `/app/account` — **Your account**, `Web App.dc.html` §2.15 (`showAccount`, lines 911–966).
 *
 * ## The merge, and what actually moved
 *
 * This screen and `/app/profile` were two screens with one subject. §2.15 draws one: a **profile
 * card** on the left — the photo, the identity fields, the email address and the bio — with an
 * **Account Management** card and a **Log out** card beside it, and **Your registrations**
 * underneath. Phase 5 moved `MEM-006`'s photo control here and left the other screen standing;
 * **on 2026-09-12 the other screen was removed.** See *One destination, and no redirect* below.
 *
 * ## Explicit saving, and what makes Cancel mean something
 *
 * `MEM-007` AC-4: a pinned footer with Cancel and Save changes, and **nothing saves implicitly**.
 * No control on this screen calls a mutation on change, on blur or on a timer; the draft lives in
 * React state until Save, and Cancel throws it away. AC-5 is the other half — the whole set goes
 * in one operation, so a partly-valid screen commits nothing rather than committing the valid
 * fields and reporting the rest.
 *
 * **Where Save and Cancel are drawn — reversed on 2026-09-21** (TW 44145291). §2.15 draws *Save
 * changes* as a pill inside the profile card and no bar at all; AC-4 asked for a pinned footer,
 * and ruling 39 gave AC-4 the win at every width. Brad reported the result from dev as the phone
 * treatment stretched across a desktop window, which it was.
 *
 * Sumit's decision: *"Your account follows the design on desktop, with Save as a pill inside the
 * card and no bar along the bottom, and keeps the pinned Save/Cancel bar on phones."* So there is
 * **one control set**, written where §2.15 draws it — the last thing in the profile card — and
 * lifted out of flow into a pinned bar only up to `lg`. That is the shape **ruling 15** gave the
 * event page's Reserve action at the same line, and for the same reason. AC-4 is reworded to say
 * so; the bar is still the only Save on the screen, at every width, so the argument ruling 39
 * actually rested on — two controls for one operation — is untouched.
 *
 * **Cancel stays on the desktop**, which §2.15 does not draw. Sumit kept it: *"The greyed-out
 * Cancel until you change something stays as it is."* It is the one departure from §2.15 left on
 * this screen's controls.
 *
 * ## The email address is not part of Save
 *
 * It is its own request with its own confirmation round trip (AC-2), because changing it is not
 * the same kind of act as changing a company name — whoever controls the address controls
 * recovery. §2.15 draws it as a plain text input among the others, which would make it part of
 * Save; it is drawn in the same place and behaves as the criterion requires instead. The old
 * address stays authoritative until the link is used, so a member who types `gmial.com` is
 * inconvenienced rather than locked out.
 *
 * ## One destination, and no redirect — 2026-09-12
 *
 * Sumit reviewed Phase 5 in the browser and reported the merge as unfinished: *"profile is still
 * taking to old profile page"*. Offered a redirect from the old address, he answered *"we don't
 * need to redirect the old profile url, it can just be removed."*
 *
 * So `/app/profile` and `/app/profile/events` are **gone**, nothing forwards, and this screen is
 * what the desktop avatar and the phone tab bar's **You** both open. That **overturns ruling 38**,
 * which had kept the other screen on the argument that `MEM-005`'s five stamped criteria described
 * it and no ruling asked for it to go. One does now, and the criteria went with the screen: all
 * five are struck to `PLT-013`, and `docs/plans/rulings-2026-09-11.md` carries the reversal.
 *
 * **What did not come with it, and why.** §2.15 is the only drawing of this screen and it has no
 * *How others see you* band, no *Only you can see this* caption and no statistics row. So the
 * captions, the three tiles and the list behind the Events tile were removed rather than rehomed —
 * the standard's rule is that the prototype owns presentation, and importing a band it does not
 * draw would be inventing layout, not preserving it. Everything on the old screen that §2.15 *does*
 * draw was already here before this change: the photo, the name and role line, the biography, and
 * the way through to Matching preferences (the Account Management card's own row).
 *
 * ## An account with no member profile (`MEM-049`)
 *
 * `/members/me/profile` answers 404 until a member profile row exists, and so does every other
 * call that begins `await this.mustFind(userId)`. A member who signed up and stopped before the
 * profile questions has a real account, and this screen was once offering them controls whose
 * requests all answered 404.
 *
 * **This file holds no list of which those are, and that is the point.** It was patched twice —
 * once to make the page render at all, once to gate the profile form and the email change — and
 * each patch added a condition here, correct about the control in front of it. Three settings rows
 * and the biography were left, and a third report followed. A condition on a screen is a
 * restatement of an answer the service owns, and restatements go stale.
 *
 * So the composition is:
 *
 *   1. `profile-service.ts` decides, by calling `mustFind` or not.
 *   2. `MEMBER_PROFILE_SERVICE_NEEDS_PROFILE` in `@h2hspark/contracts` states that per method, and
 *      an API test derives the same table from the service's source and fails when the two differ.
 *   3. `MEMBER_ACCOUNT_CONTROL_READS` in `constants.ts` says which methods each control calls —
 *      the one fact that is genuinely the screen's.
 *   4. `shows(...)` below composes them. A method that gains or loses `mustFind` fails a build and
 *      then moves every control that calls it, in one edit, in `contracts`.
 *
 * The rule the story states exactly: **a control goes when its own request needs a profile.** Not
 * when it looks related to one — Tickets & orders reads orders and stays, and hiding it would
 * withhold something a member can use, which is the mirror of the defect and no better.
 *
 * ## Sign out is here, and the prototype draws it here too
 *
 * `USR-007` AC-3 — *"A member ends the session on the device they are using"*. It was on the old
 * `/app/profile` account form, moved here when `MEM-005`'s profile replaced that screen, and §2.15
 * puts it exactly where it now is: its own card under Account Management, a full-width outlined
 * control in `--danger`, labelled **Log out**. One control, on one screen; a second copy is a
 * second thing to keep in step.
 *
 * The sub-line is **longer** than §2.15's *"You'll be signed out on this device only."*, and the
 * extra clause is the criterion: AC-3's whole distinction is against AC-4's central revocation, so
 * the sentence says what happens to the member's *other* devices rather than leaving it inferred.
 * `MEMBER_ACCOUNT_COPY.signOutSub` carries the reasoning.
 *
 * ## The two controls §2.15 puts in the header, and what they open
 *
 * *Show my connection QR code* and *Scan* (`MEM-057`, ADR 053). While the member is at an event
 * that is running, both open **that event's** Connect surface and its event-scoped code (AC-1);
 * otherwise the first shows the member's **profile** code, which works outside any event, and the
 * second opens the scanner. Which of those applies is decided from the member's own registrations
 * by the server, on the connect-code surface — see `member-connect-code/ports/live-event-port.ts`
 * for why a client cannot answer it.
 */
import { LogoutButton } from '@h2hspark/auth-ui';
import type { MemberOwnProfileView } from '@h2hspark/contracts';
import {
  Alert,
  ApiRequestError,
  Button,
  Field,
  Input,
  Skeleton,
  Textarea,
  cn,
  useErrorToast,
} from '@h2hspark/ui';
import {
  ChevronRight,
  Lock,
  LogOut,
  QrCode,
  Share2,
  ShieldCheck,
  Sliders,
  Ticket,
  X,
} from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  MEMBER_SAVE_BAR_CLEARANCE_CLASS,
  MEMBER_SAVE_BAR_OFFSET_CLASS,
  MEMBER_SAVE_BAR_UNPINS_CLASS,
} from '../../member/pinned-bar-geometry';
import { InstallAppCard } from '../../member/pwa/install-app-card';
import { roleAtCompany } from '../attendee-preview/constants';
import { YourRegistrationsCard } from '../event-orders/your-registrations-card';
import { useDismissProfileScanNotice, useMyConnectCode } from '../member-connect-code/api';
import {
  DISMISS_NOTICE_LABEL,
  NOTICES_HEADING,
  SHOW_MY_CODE_LABEL,
  connectAtEventLabel,
} from '../member-connect-code/constants';
import { MyConnectionCodeDialog } from '../member-connect-code/my-connection-code-dialog';
import { ProfileScanPanel } from '../member-connect-code/profile-scan-panel';
import { connectPath } from '../member-in-event/constants';

import {
  useCancelAccountDeletion,
  useMemberAppAdmission,
  useOwnProfile,
  useRequestAccountDeletion,
  useRequestEmailChange,
  useSaveAccount,
} from './api';
import {
  MEMBER_ACCOUNT_COPY,
  MEMBER_PROFILE_ROUTES,
  MEMBER_PROFILE_SETUP_PATH,
  memberAccountControlWorksWithoutProfile,
  memberAccountSettingsRowsFor,
  type MemberAccountControlId,
  type MemberAccountSettingsRow,
} from './constants';
import { ProfilePhotoForm } from './profile-photo-form';

const ROW_ICONS: Record<string, React.ReactNode> = {
  password: <Lock aria-hidden className="h-[18px] w-[18px]" />,
  privacy: <ShieldCheck aria-hidden className="h-[18px] w-[18px]" />,
  'matching-preferences': <Sliders aria-hidden className="h-[18px] w-[18px]" />,
  'social-channels': <Share2 aria-hidden className="h-[18px] w-[18px]" />,
  tickets: <Ticket aria-hidden className="h-[18px] w-[18px]" />,
};

/** §2.15's card treatment, used by all four cards on this screen so they cannot drift apart. */
const ACCOUNT_CARD = 'rounded-xl border border-border-subtle bg-card shadow-sm';

/**
 * **§2.15's field control**, and the reason it is a constant.
 *
 * The prototype's account inputs are `height:50px; background:var(--surface-page-alt);
 * border:1.5px solid var(--border-default); border-radius:var(--radius-md); padding:0 14px;
 * font-size:var(--text-md)`. The shipped screen used `Input`'s own defaults — 36px, transparent,
 * a 1px `--input` hairline, `--text-sm` and 12px of padding — which is the admin console's control
 * (`field.tsx`'s header explains why the two consoles keep separate metrics) and is visibly a
 * different, tighter form than the one drawn. This is what Sumit meant by *"some elements of your
 * account does not match the prototype styling"*: read side by side the difference is a third of
 * the field's height.
 *
 * Written once and passed to all six controls on the card — the four identity fields, the email
 * row and the biography — so the screen cannot come to have two field shapes on it, which is the
 * *check the neighbours* step of `docs/standards/building-from-designs.md`. The textarea overrides
 * only the height, because §2.15 gives it `min-height:90px` and vertical padding and everything
 * else about it is the same control.
 */
const ACCOUNT_FIELD =
  'h-[50px] rounded-md border-[1.5px] border-border bg-ivory-100 px-[14px] text-md text-text-body ' +
  'shadow-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/40';

/** The same control as a textarea: §2.15's `min-height:90px` with 13px/14px padding. */
const ACCOUNT_TEXTAREA =
  'min-h-[90px] resize-none rounded-md border-[1.5px] border-border bg-ivory-100 px-[14px] py-[13px] ' +
  'text-md leading-normal text-text-body shadow-none focus-visible:border-primary ' +
  'focus-visible:ring-2 focus-visible:ring-ring/40';

/** §2.15's label: `font-brand` 600 at `--text-sm` in `--text-strong`, with 7px under it. */
const ACCOUNT_FIELD_LABEL = 'gap-[7px]';

interface AccountDraft {
  firstName: string;
  lastName: string;
  role: string;
  company: string;
  bio: string;
}

/** An account with no member profile yet (`MEM-049`). Also what Cancel resets such a draft to. */
const EMPTY_DRAFT: AccountDraft = { firstName: '', lastName: '', role: '', company: '', bio: '' };

/** The saved set, as the form holds it. One function, so seeding and Cancel cannot disagree. */
function draftOf(profile: { readonly shared: MemberOwnProfileView['shared'] }): AccountDraft {
  const { shared } = profile;
  return {
    firstName: shared.firstName,
    lastName: shared.lastName,
    role: shared.role ?? '',
    company: shared.company ?? '',
    bio: shared.bio ?? '',
  };
}

export function MyAccountPage(): React.ReactElement {
  const profile = useOwnProfile();
  const save = useSaveAccount();
  const navigate = useNavigate();
  const showError = useErrorToast();

  const [draft, setDraft] = React.useState<AccountDraft | null>(null);
  const [saved, setSaved] = React.useState(false);

  // Read-only: seeds the draft once. It never writes, and it never re-seeds over a member's
  // typing — a refetch that overwrote a half-typed field would be the autosave problem wearing a
  // different hat.
  React.useEffect(() => {
    if (draft !== null) return;
    if (profile.data !== undefined) {
      setDraft(draftOf(profile.data));
      return;
    }
    // **A member who has not finished their profile still has an account.**
    //
    // `/members/me/profile` answers 404 until a member profile exists — `mustFind` throws
    // `ResourceNotFoundError` — and the query is `retry: false`, so it settles as an error with no
    // data. The draft was therefore never seeded, and the early return below rendered a skeleton
    // that never resolved: an empty box, for exactly the account this app spends its previous
    // screen inviting somebody to finish.
    //
    // It also hid **sign out**, which lives on this screen and on no other. A member who signed
    // up, stopped before the profile questions and wanted to leave had no way to, on any screen.
    //
    // So a 404 seeds an empty draft and the page renders. Every other failure does **not** — see
    // the branch below. An unreachable API must not be shown as a blank profile, because saving
    // that would overwrite a real one with nothing.
    if (isProfileNotFound(profile.error)) setDraft(EMPTY_DRAFT);
  }, [draft, profile.data, profile.error]);

  /**
   * What Cancel does (`MEM-007` AC-4): put the saved values back.
   *
   * The saved values are `profile.data`'s, which is the same object the draft was seeded from, so
   * this is the seeding read run again rather than a second idea of what "unchanged" means. An
   * account with no profile resets to the empty draft it started with.
   */
  const resetDraft = (): void => {
    setSaved(false);
    setDraft(profile.data === undefined ? EMPTY_DRAFT : draftOf(profile.data));
  };

  React.useEffect(() => {
    if (save.error !== null) showError(save.error, 'That did not save. Try again.');
  }, [save.error, showError]);

  // **What this account can actually do before the profile questions are answered** (`MEM-049`).
  //
  // The screen does not decide. `shows` composes two facts it does not own: whether this member
  // has a profile, and — from `MEMBER_ACCOUNT_CONTROL_READS` in `constants.ts` and
  // `MEMBER_PROFILE_SERVICE_NEEDS_PROFILE` in `contracts` — whether the control's own request
  // needs one. There is no list of profile-requiring controls in this file, and that absence is
  // AC-3.
  const hasProfile = profile.data !== undefined;
  const shows = (control: MemberAccountControlId): boolean =>
    hasProfile || memberAccountControlWorksWithoutProfile(control);

  if (profile.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }
  // A failure that is not "no profile yet" is reported rather than drawn as an empty account.
  if (draft === null) {
    return (
      <Alert variant="destructive">
        <p>{MEMBER_ACCOUNT_COPY.couldNotLoad}</p>
        <Button className="mt-3" onClick={() => void profile.refetch()} variant="secondary">
          {MEMBER_ACCOUNT_COPY.couldNotLoadRetry}
        </Button>
      </Alert>
    );
  }

  const set = (field: keyof AccountDraft) => (value: string) => {
    setSaved(false);
    setDraft((current) => (current === null ? current : { ...current, [field]: value }));
  };

  const complete =
    draft.firstName.trim() !== '' &&
    draft.lastName.trim() !== '' &&
    draft.role.trim() !== '' &&
    draft.company.trim() !== '';

  /** Whether Cancel has anything to throw away. Compared field by field against the saved set. */
  const saved0 = profile.data === undefined ? EMPTY_DRAFT : draftOf(profile.data);
  const dirty = (Object.keys(saved0) as ReadonlyArray<keyof AccountDraft>).some(
    (field) => saved0[field] !== draft[field],
  );

  const displayName = `${draft.firstName} ${draft.lastName}`.trim();
  const roleLine = roleAtCompany({
    role: draft.role.trim() === '' ? null : draft.role.trim(),
    company: draft.company.trim() === '' ? null : draft.company.trim(),
  });

  return (
    <form
      // The bottom padding buys back the space the pinned footer takes out of flow. It is a
      // named constant with its arithmetic beside it rather than a number chosen by eye — see
      // `member/pinned-bar-geometry.ts`. It is the footer's, so it is conditional on the same
      // answer: a screen with no pinned footer must not reserve a footer's worth of empty space
      // under its last card. The class carries an `lg:` of its own for the width where the
      // controls are in flow in the card and there is no bar to clear (TW 44145291).
      //
      // Marked, so `a-pinned-bar-does-not-eat-the-page.test.tsx` finds the element that pays for
      // the bar rather than the bar's parent — which since 2026-09-21 is the profile card, where
      // §2.15 draws the controls.
      data-pinned-bar-clearance=""
      className={cn(shows('identity') ? MEMBER_SAVE_BAR_CLEARANCE_CLASS : 'pb-8')}
      onSubmit={(event) => {
        event.preventDefault();
        // One operation, the whole screen (AC-5). There is no per-field call anywhere here.
        save.mutate(
          {
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            role: draft.role.trim(),
            company: draft.company.trim(),
            bio: draft.bio.trim(),
          },
          { onSuccess: () => setSaved(true) },
        );
      }}
    >
      {/* §2.15's header row: the title and its lede at the left, the two connect-code controls at
          the right, wrapping under it on a narrow screen. */}
      <header className="mb-7 flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <h1 className="mb-1.5 text-2xl tracking-tight md:text-4xl">
            {MEMBER_ACCOUNT_COPY.title}
          </h1>
          <p className="text-md text-muted-foreground md:text-lg">{MEMBER_ACCOUNT_COPY.blurb}</p>
        </div>
        {shows('photo') ? (
          <div className="flex flex-wrap items-center gap-2.5">
            <ConnectCodeControls fullName={displayName} roleLine={roleLine} />
          </div>
        ) : null}
      </header>

      {/* `MEM-057` AC-6 — somebody scanned this member's profile code. Above the fold, because it
          is the one thing on this screen the member did not do themselves. */}
      {shows('photo') ? <ProfileScanNotices /> : null}

      <div className="grid grid-cols-1 items-start gap-7 lg:grid-cols-[1.4fr_minmax(0,1fr)]">
        {/* AC-4. One ask, and it is the platform's. It stands in for every control the
            composition below withheld, and is drawn once however many those are. **It takes the
            profile card's place, in the main column** (TW 44206262, 2026-09-25): drawn inside the
            right column, it left the grid's first track to that column, and Account Management
            and Log out slid across into the main one. */}
        {hasProfile ? null : <FinishYourProfileCard />}

        {/* ── the profile card ───────────────────────────────────────────────────────────── */}
        {shows('identity') ? (
          <section className={cn(ACCOUNT_CARD, 'p-6 md:p-[26px]')}>
            <h2 className="sr-only">{MEMBER_ACCOUNT_COPY.profileCardHeading}</h2>
            <ProfilePhotoForm
              photoUrl={profile.data?.shared.photoUrl ?? null}
              fullName={displayName}
              roleLine={roleLine}
            />

            {/* §2.15's two-up field rows. They collapse to one column below `sm`, where a pair of
                labelled inputs truncates its labels — the defect the parity report found across
                the app, and the reason this screen used to stack them at every width. */}
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="First name" htmlFor="account-first-name" className={ACCOUNT_FIELD_LABEL}>
                <Input
                  id="account-first-name"
                  className={ACCOUNT_FIELD}
                  value={draft.firstName}
                  onChange={(event) => set('firstName')(event.target.value)}
                  autoComplete="given-name"
                />
              </Field>
              <Field label="Last name" htmlFor="account-last-name" className={ACCOUNT_FIELD_LABEL}>
                <Input
                  id="account-last-name"
                  className={ACCOUNT_FIELD}
                  value={draft.lastName}
                  onChange={(event) => set('lastName')(event.target.value)}
                  autoComplete="family-name"
                />
              </Field>
            </div>
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Role" htmlFor="account-role" className={ACCOUNT_FIELD_LABEL}>
                <Input
                  id="account-role"
                  className={ACCOUNT_FIELD}
                  value={draft.role}
                  onChange={(event) => set('role')(event.target.value)}
                  autoComplete="organization-title"
                />
              </Field>
              <Field label="Company" htmlFor="account-company" className={ACCOUNT_FIELD_LABEL}>
                <Input
                  id="account-company"
                  className={ACCOUNT_FIELD}
                  value={draft.company}
                  onChange={(event) => set('company')(event.target.value)}
                  autoComplete="organization"
                />
              </Field>
            </div>

            {shows('email') ? (
              <EmailAddressRow
                emailAddress={profile.data?.private.emailAddress ?? ''}
                pending={profile.data?.private.pendingEmailAddress ?? null}
                verified={profile.data?.private.emailVerified === true}
              />
            ) : null}

            {/* The biography, in §2.15's own place at the foot of the profile card rather than in
                a card of its own. It is part of the identity card's one save — `saveAccount`
                carries both — which is what its `MEMBER_ACCOUNT_CONTROL_READS` entry says, and
                separating them on screen while joining them on the wire is exactly what let an
                earlier fix gate the fields and leave this behind. */}
            {shows('biography') ? (
              <Field
                label={MEMBER_ACCOUNT_COPY.bioHeading}
                htmlFor="account-bio"
                className={ACCOUNT_FIELD_LABEL}
              >
                <Textarea
                  id="account-bio"
                  className={ACCOUNT_TEXTAREA}
                  value={draft.bio}
                  placeholder={MEMBER_ACCOUNT_COPY.bioPlaceholder}
                  onChange={(event) => set('bio')(event.target.value)}
                />
                <p className="mt-1 text-right text-xs text-muted-foreground">
                  {draft.bio.length}/600
                </p>
              </Field>
            ) : null}

            {/* ── Cancel and Save changes ─────────────────────────────────────────────────
                `MEM-007` AC-4, as Sumit decided it on 2026-09-21 (TW 44145291).

                **One control set, in the card, drawn as a bar only where there is no room for
                it in the card.** §2.15 puts *Save changes* as a pill inside the profile card,
                straight under the Bio, and draws no bar at all; AC-4 asked for a pinned footer
                at every width, and until now the bar won. Brad reported the result as the phone
                treatment stretched across a desktop window, and it was.

                So this is the shape **ruling 15** gave the event page's Reserve action, applied
                here: the markup sits where the design draws it — the last thing in the profile
                card — and is `fixed` to the foot of the viewport up to `lg`, `lg:static` from
                there. `lg` is the same line the two-column layout opens at, which is not a
                coincidence: below it the card is the width of the screen and its foot is a long
                scroll away, so Save is pinned; at it the card is a column with its own end in
                view.

                Nothing is added or taken away at any width — the same two controls, in the same
                order, doing the same thing — so it is reflow and earns no `responsive-behaviour`
                entry, exactly as the reserve action does not.

                **Cancel stays on the desktop too**, though §2.15 draws none. Sumit's decision
                keeps it — *"The greyed-out Cancel until you change something stays as it is"* —
                and a member who typed something they did not mean would otherwise have no way
                back to their saved values. Recorded as the one departure from §2.15 here.

                **It is the identity card's control, so it goes when that card goes** (`MEM-049`
                AC-1): Save posts `saveAccount`, which needs a profile, and it is now literally
                inside the card rather than merely conditional on it. */}
            <div
              className={cn(
                'fixed inset-x-0 z-20 border-t border-border bg-card/95 px-4 py-3 backdrop-blur',
                MEMBER_SAVE_BAR_UNPINS_CLASS,
                MEMBER_SAVE_BAR_OFFSET_CLASS,
              )}
            >
              <div className="mx-auto flex max-w-app-desktop items-center gap-3 lg:max-w-none">
                {/*
                  **Cancel discards the draft; it does not navigate.**

                  It used to `navigate(MEMBER_PROFILE_ROUTES.profile)`, which was Cancel meaning
                  *leave this screen* — reasonable while there was a profile screen behind this
                  one to leave to, and impossible now that there is not (`/app/profile` is
                  removed). It is also what this file's own header said Cancel was for: *"the
                  draft lives in React state until Save, and Cancel throws it away."* Now it does
                  exactly that, which is `MEM-007` AC-4's *"nothing saves implicitly"* seen from
                  the other side — a member who typed something they did not mean gets the saved
                  values back without losing the page.
                */}
                <Button
                  type="button"
                  variant="outline-strong"
                  size="pill"
                  className="flex-none"
                  disabled={!dirty || save.isPending}
                  onClick={resetDraft}
                >
                  {MEMBER_ACCOUNT_COPY.cancel}
                </Button>
                {/* §2.15's *Save changes* is `--text-md` on `padding:13px 24px`, which is what
                    `pill-lg` and `text-md` are. It kept that scale in the bar and keeps it in
                    the card, so the one Save on the screen is the size the design drew it.
                    `sm:flex-none` stops it filling the card's width on the desktop, where §2.15
                    draws a pill the width of its own label. */}
                <Button
                  type="submit"
                  size="pill-lg"
                  className="flex-1 text-md sm:flex-none sm:px-6"
                  disabled={!complete || save.isPending}
                >
                  {save.isPending ? 'Saving…' : MEMBER_ACCOUNT_COPY.save}
                </Button>
                {saved ? (
                  <p role="status" className="text-xs text-success">
                    {MEMBER_ACCOUNT_COPY.saved}
                  </p>
                ) : null}
              </div>
            </div>
          </section>
        ) : null}

        {/* ── the right column ──────────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-5">
          <section className={cn(ACCOUNT_CARD, 'p-5')}>
            <h2 className="mb-3.5 text-md">{MEMBER_ACCOUNT_COPY.accountManagementHeading}</h2>
            {/* §3.20's rows carry 10px between them and none under the last, which is the
                prototype's own markup: the first three rows have `margin-bottom:10px` and the
                fourth has none. A uniform margin left the card 10px taller than it is drawn. */}
            <ul className="space-y-2.5">
              {memberAccountSettingsRowsFor(hasProfile).map((row) => (
                <li key={row.id}>
                  <SettingsRow row={row} icon={ROW_ICONS[row.id]} />
                </li>
              ))}
            </ul>
          </section>

          {/*
            `USR-007` AC-3 — the member ends the session on the device they are using, in §2.15's
            own card and with §2.15's own sentence under it.

            `LogoutButton` from `@h2hspark/auth-ui` rather than a button wired to a hook here: the
            package owns what signing out *is* — `POST /auth/logout`, then `auth.sessionToken`
            cleared whether or not the server answered, because a member who pressed this wants to
            be signed out of this browser regardless of what the network did.

            It renders `type="button"`, which matters here and nowhere else in the app: this
            control sits inside the account form, and a default-typed button there would submit
            the draft on the way out.
          */}
          <section className={cn(ACCOUNT_CARD, 'p-5')}>
            <h2 className="sr-only">{MEMBER_ACCOUNT_COPY.signOutHeading}</h2>
            {/* §2.15's Log out is a full-width **outlined** control in `--danger`, which is not
                one of `LogoutButton`'s three variants. `secondary` is the closest — an outlined
                button — and the two classes below are the prototype's own border weight and
                colour. Restyled rather than reimplemented: the package owns what signing out
                *is*, and a second copy of that here is a second place a session gets cleared. */}
            <LogoutButton
              variant="secondary"
              className="flex w-full items-center justify-center gap-[7px] rounded-pill border-[1.5px] border-border py-3 text-destructive"
              onLoggedOut={() => navigate(MEMBER_PROFILE_ROUTES.signIn, { replace: true })}
            >
              <LogOut aria-hidden className="h-4 w-4" />
              {MEMBER_ACCOUNT_COPY.signOut}
            </LogoutButton>
            <p className="mt-2 px-0.5 text-xs leading-snug text-text-subtle">
              {MEMBER_ACCOUNT_COPY.signOutSub}
            </p>
          </section>

          {/*
            The install control (`MEM-040` AC-4, `MEM-043` AC-1). A row the member navigated to
            rather than a banner that found them — `pwa/install-app-card.tsx` explains why that is
            the only surface this story may build. It renders nothing at all on a browser with no
            install story, including inside the installed app itself.
          */}
          <InstallAppCard />

          {/*
            `USR-011` — asking to be deleted, and taking it back.

            **The prototype does not draw this**, anywhere: the only `delete` copy in either file
            belongs to the Disconnect confirmation. So the card is built from the story in the
            shape of the Log out card above it — same treatment, same one-sentence sub-line —
            rather than inventing a red-bordered danger zone the design system has no precedent
            for. Named as an addition in the pull request; it is not a divergence, because there is
            nothing to diverge from.

            Every string is `MEMBER_ACCOUNT_COPY`'s, and that header carries `USR-011` AC-7:
            nothing here may say the member's data has been erased, because ADR 030 says none of it
            is.
          */}
          {shows('deletion') ? (
            <AccountDeletionCard requestedAt={profile.data?.private.deletionRequestedAt ?? null} />
          ) : null}
        </div>
      </div>

      {/*
        `MEM-022` AC-6. The web account page's own registrations block, rendering **the same data**
        as the PWA's Tickets & orders — `MEM-TKT-015` says so in as many words, and the story's
        Notes say what happens otherwise: "Two renderers over one source; not two sources."

        §2.15 puts it full width under the two columns, which is where it is.
      */}
      <div className="mt-7">
        <YourRegistrationsCard />
      </div>

    </form>
  );
}

/**
 * §2.15's two header controls (`MEM-057` AC-1, AC-5).
 *
 * ## The branch, and why it is decided here rather than on the server
 *
 * AC-1: while the member is **at an event that has not ended**, both controls open that event's
 * Connect surface and its event-scoped code. Otherwise the first shows the profile code and the
 * second opens the scanner.
 *
 * That question — *am I at an event right now* — is answered from the member's own registrations,
 * which this app already holds for the registrations card at the foot of this screen. A server
 * route for it would be a new read across every registration and every event, for a fact one
 * existing payload already carries. `useMyRegisteredEventHappeningNow` is the derivation, and it
 * is in `api.ts` beside the query it reads so it can be tested without a component.
 *
 * **It is "registered and running", not "checked in".** Check-in is the door's business, and the
 * Connect surface itself is where a member who has not been scanned in is told so, in the
 * server's own words (`MEM-031` AC-5). Deciding it here as well would be this screen restating an
 * answer another screen owns, and getting it wrong in the direction that matters — sending a
 * member to their profile code when they are standing in a room.
 */
function ConnectCodeControls({
  fullName,
  roleLine,
}: {
  readonly fullName: string;
  readonly roleLine: string | null;
}): React.ReactElement {
  const surface = useMyConnectCode();
  const atEvent = surface.data?.surface.atEvent ?? null;

  if (atEvent !== null) {
    // One destination for both controls: that event's Connect surface carries the event-scoped
    // code *and* its own scanner, gated on the same device check this screen would apply. Two
    // buttons pointing at one screen would be two ways to say the same thing.
    return (
      <Button asChild className="rounded-pill">
        <Link to={connectPath(atEvent.eventId)}>
          <QrCode aria-hidden className="mr-2 h-[17px] w-[17px]" />
          {connectAtEventLabel(atEvent.eventName)}
        </Link>
      </Button>
    );
  }

  return (
    <>
      <MyConnectionCodeDialog fullName={fullName} roleLine={roleLine}>
        <button
          type="button"
          className="inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-pill bg-spark px-[18px] py-3 font-brand text-sm font-bold text-white shadow-accent"
        >
          <QrCode className="h-[17px] w-[17px]" aria-hidden />
          {SHOW_MY_CODE_LABEL}
        </button>
      </MyConnectionCodeDialog>
      <ProfileScanPanel />
    </>
  );
}

/**
 * `MEM-057` AC-6 — *"{name} logged your connection."*, for a scan of this member's profile code.
 *
 * The same sentence the in-event surface uses, composed by the server from the same function, and
 * carrying the same silences: no note, no count, no timestamp, and nothing at all back to the
 * member who scanned. Dismissing one writes on the reader's own connection row and tells the
 * scanner nothing.
 *
 * It renders nothing when there are none, which is almost always.
 */
function ProfileScanNotices(): React.ReactElement | null {
  const surface = useMyConnectCode();
  const dismiss = useDismissProfileScanNotice();
  const notices = surface.data?.surface.notices ?? [];

  if (notices.length === 0) return null;

  return (
    <section className="mb-7 rounded-lg border border-brand-100 bg-brand-50 p-4">
      <h2 className="mb-2 font-brand text-2xs font-bold uppercase tracking-caps text-primary">
        {NOTICES_HEADING}
      </h2>
      <ul className="space-y-2">
        {notices.map((notice) => (
          <li key={notice.connectionRef} className="flex items-center gap-3">
            <Link
              className="min-w-0 flex-1 text-sm text-text-body hover:underline"
              to={`/app/connections/${notice.connectionRef}`}
            >
              {notice.message}
            </Link>
            <button
              type="button"
              aria-label={`${DISMISS_NOTICE_LABEL} — ${notice.message}`}
              disabled={dismiss.isPending}
              onClick={() => {
                dismiss.mutate(notice.connectionRef);
              }}
              className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-pill text-text-subtle hover:bg-card"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The email address and its confirmation round trip (`MEM-007` AC-2, AC-7), in §2.15's own place.
 *
 * **The notice is the same sentence whatever the address turns out to be.** There is no branch
 * here on whether it is taken, because the API does not tell this screen — a client that could
 * say "that address is already in use" would be the enumeration oracle the invariant forbids,
 * however helpful it looked.
 *
 * §2.15 draws a plain `<input value="deb@h2hspark.com">` in this slot, which would make the
 * address part of Save. It is drawn here as the address with a *Change email* control beside it
 * instead: AC-2 requires the change to be re-verified, and a field that saved with the rest would
 * move an account's recovery address on the same tap as a job title.
 */
function EmailAddressRow({
  emailAddress,
  pending,
  verified,
}: {
  readonly emailAddress: string;
  readonly pending: string | null;
  /** `USR-003` AC-3 — the application's own `emailVerifiedAt`, served on the profile payload. */
  readonly verified: boolean;
}): React.ReactElement {
  const request = useRequestEmailChange();
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState('');

  return (
    <div className="mb-4">
      <span className="mb-[7px] block font-brand text-sm font-semibold text-foreground">
        {MEMBER_ACCOUNT_COPY.emailHeading}
      </span>
      {/* §2.15's field, at its own metrics — 50px minimum, `--surface-page-alt`, a 1.5px
          `--border-default` edge, 14px of side padding — holding text rather than an input, which
          is ruling 40. The control beside it is the screen's secondary pill at row scale. */}
      <div className="flex min-h-[50px] flex-wrap items-center gap-2 rounded-md border-[1.5px] border-border bg-ivory-100 px-[14px] py-2">
        <span className="min-w-0 flex-1 break-all text-md text-text-body">{emailAddress}</span>
        {editing ? null : (
          <Button
            type="button"
            variant="outline-strong"
            size="sm"
            className="rounded-pill font-brand font-bold"
            onClick={() => setEditing(true)}
          >
            {MEMBER_ACCOUNT_COPY.emailChangeAction}
          </Button>
        )}
      </div>

      {/*
        `USR-003` AC-5's entry point, and the only place in the member app that says an address is
        unproved. **A line and a link rather than a banner or a modal**: verification is
        non-blocking (ADR 028), so a treatment that interrupted would be telling the member their
        account is stuck when it is not.
      */}
      {!verified ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          This address is not verified yet.{' '}
          <Link className="auth-ui-link" to={MEMBER_PROFILE_ROUTES.verifyEmail}>
            {MEMBER_ACCOUNT_COPY.emailVerifyAction}
          </Link>
        </p>
      ) : null}

      {pending !== null && !request.isSuccess ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Waiting on confirmation for <span className="break-all">{pending}</span>. Until you use
          the link, you keep signing in with the address above.
        </p>
      ) : null}

      {editing ? (
        <div className="mt-3 space-y-3 rounded-md border border-border-subtle p-3.5">
          <p className="text-xs text-muted-foreground">{MEMBER_ACCOUNT_COPY.emailChangeHint}</p>
          <Field label="New email address" htmlFor="account-new-email">
            <Input
              id="account-new-email"
              type="email"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              autoComplete="email"
            />
          </Field>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditing(false);
                setValue('');
              }}
            >
              {MEMBER_ACCOUNT_COPY.cancel}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={value.trim() === '' || request.isPending}
              onClick={() => request.mutate({ emailAddress: value.trim() })}
            >
              {request.isPending ? 'Sending…' : 'Send the link'}
            </Button>
          </div>
        </div>
      ) : null}

      {request.isSuccess ? (
        <p role="status" className="mt-1.5 text-sm text-foreground">
          {MEMBER_ACCOUNT_COPY.emailChangeNotice}
        </p>
      ) : null}
    </div>
  );
}

/**
 * **Asking to be deleted, and taking it back** (`USR-011` AC-1, AC-3, AC-7).
 *
 * ## Two states, and neither of them takes anything away
 *
 * With no outstanding request the card offers one. With a request outstanding it says so, says the
 * member may still use the account, and offers the way back — which is AC-3, and the reason
 * `deletion_requested` can sign in at all: *"a deletion flow with no way back turns a mis-tap into
 * a destroyed account."*
 *
 * ## The confirm step is a second tap, not a modal
 *
 * The request is reversible by design, so a blocking dialog would be ceremony out of proportion to
 * the consequence. What this screen does have is the pattern the email row above it uses: the
 * control reveals a short confirmation and two buttons. A mis-tap costs a second tap; it does not
 * cost an account, and it never could.
 *
 * ## Every word is AC-7
 *
 * *Request*, *recorded*, *reviewed*, *access ends*. Nothing here says deleted, erased or
 * permanently, because ADR 030 is that **no member data is purged in MVP** and the story is blunt
 * about the margin: *"a soft delete described to the member as an erasure"* is the one part of
 * this posture that would be a problem rather than a deferral.
 *
 * The buttons are `type="button"`. This card sits inside the account `<form>`, where a
 * default-typed button is a submit button.
 */
function AccountDeletionCard({
  requestedAt,
}: {
  /** When the member asked, or `null` for no outstanding request. */
  readonly requestedAt: string | null;
}): React.ReactElement {
  const request = useRequestAccountDeletion();
  const cancel = useCancelAccountDeletion();
  const [confirming, setConfirming] = React.useState(false);

  // The request is outstanding the moment the server says so. `cancel.isSuccess` is not consulted
  // here: cancelling invalidates the profile, so `requestedAt` becomes `null` on its own, and a
  // second source of truth for "is there a request" is how the two would come to disagree.
  const outstanding = requestedAt !== null;

  return (
    <section className={cn(ACCOUNT_CARD, 'space-y-3 p-5')}>
      <h2 className="text-2xs font-brand font-semibold uppercase tracking-caps text-muted-foreground">
        {outstanding
          ? MEMBER_ACCOUNT_COPY.deletionPendingHeading
          : MEMBER_ACCOUNT_COPY.deletionHeading}
      </h2>
      <p className="text-sm text-muted-foreground">
        {outstanding ? MEMBER_ACCOUNT_COPY.deletionPendingSub : MEMBER_ACCOUNT_COPY.deletionSub}
      </p>

      {outstanding ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          {cancel.isPending ? 'Withdrawing…' : MEMBER_ACCOUNT_COPY.deletionCancelAction}
        </Button>
      ) : confirming ? (
        <div className="space-y-3">
          <p className="text-sm text-foreground">{MEMBER_ACCOUNT_COPY.deletionConfirmPrompt}</p>
          <p className="text-xs text-muted-foreground">
            {MEMBER_ACCOUNT_COPY.deletionConfirmBody}
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setConfirming(false)}>
              {MEMBER_ACCOUNT_COPY.cancel}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={request.isPending}
              onClick={() => request.mutate(undefined, { onSuccess: () => setConfirming(false) })}
            >
              {request.isPending ? 'Sending…' : MEMBER_ACCOUNT_COPY.deletionConfirm}
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setConfirming(true)}>
          {MEMBER_ACCOUNT_COPY.deletionAction}
        </Button>
      )}

      {cancel.isSuccess && !outstanding ? (
        <p role="status" className="text-sm text-foreground">
          {MEMBER_ACCOUNT_COPY.deletionCancelled}
        </p>
      ) : null}
    </section>
  );
}

/** §3.20's settings nav row: an 18px brand-primary icon, the label, and a chevron. */
function SettingsRow({
  row,
  icon,
}: {
  readonly row: MemberAccountSettingsRow;
  readonly icon: React.ReactNode;
}): React.ReactElement {
  const body = (
    <>
      <span className="flex-none text-primary">{icon}</span>
      <span className="flex-1 text-left font-brand text-sm font-bold text-foreground">
        {row.label}
      </span>
    </>
  );

  if (row.href === null) {
    // Named, not dead. The story that delivers it is on the row.
    return (
      <div className="flex items-center gap-3 rounded-md border border-border-subtle px-3.5 py-[13px] opacity-60">
        {body}
        <span className="flex-none text-2xs uppercase tracking-caps text-muted-foreground">
          Not built · {'notBuilt' in row ? row.notBuilt : ''}
        </span>
      </div>
    );
  }

  return (
    <Link
      to={row.href}
      className="flex items-center gap-3 rounded-md border border-border-subtle bg-card px-3.5 py-[13px] transition-colors hover:bg-muted"
    >
      {body}
      <ChevronRight aria-hidden className="h-4 w-4 flex-none text-text-subtle" />
    </Link>
  );
}

/**
 * Is this the API saying "this member has no profile yet"?
 *
 * A 404 from `/members/me/profile` is a **state**, not a failure: the account exists and the
 * member has not answered the profile questions. Everything else — a 500, an unreachable API — is
 * a failure, and the difference decides whether this screen offers an empty profile to fill in or
 * says it cannot load. Getting it wrong in the other direction would let a member save blanks
 * over a profile that was merely unreachable.
 */
function isProfileNotFound(error: unknown): boolean {
  return error instanceof ApiRequestError && error.statusCode === 404;
}

/**
 * What Your account offers a member who has not answered the profile questions.
 *
 * Not a second version of the ask. `useMemberAppAdmission` is where the wording lives — the same
 * ask the You tab renders, written once on the server — and `MEMBER_PROFILE_SETUP_PATH` is the
 * same wizard with the same `returnTo` contract, so finishing it lands the member back here.
 *
 * It stands in for whatever the screen withheld, and it does not enumerate that — `shows(...)`
 * does, out of `MEMBER_PROFILE_SERVICE_NEEDS_PROFILE`. Drawn once whether one control went or six,
 * because the member's next move is the same in every case: finish the profile.
 */
function FinishYourProfileCard(): React.ReactElement {
  const admission = useMemberAppAdmission(true);
  const ask = admission.data?.ask ?? null;

  return (
    <section className={cn(ACCOUNT_CARD, 'space-y-3 p-5')}>
      {/*
        **All three of the server's sentences, since 2026-09-12** (`USR-014` AC-5, AC-7).

        This card drew the body alone, because the heading and the reassurance were on the You
        tab's own version of the ask — `/app/profile`, which rendered `heading`, `body` and
        `reassurance` in AC-5's order. That screen is removed and this card is the **only** door
        onto the ask now, so it renders what the tab rendered. Dropping two of the three sentences
        on the way would have been the merge quietly narrowing a criterion.

        Still the server's words, still one ask (`useMemberAppAdmission`), still no second
        onboarding — what changed is which screen draws them.
      */}
      <h2 className="text-2xs font-brand font-semibold uppercase tracking-caps text-muted-foreground">
        {MEMBER_ACCOUNT_COPY.identityHeading}
      </h2>
      {ask === null ? null : (
        <p className="font-brand text-md font-bold text-foreground">{ask.heading}</p>
      )}
      {/* The server's ask when it has arrived, and a plain sentence while it has not. Never a
          spinner: this card is not the reason anybody opened this screen, and a member who came
          here to sign out should not wait on a request that has nothing to do with it. */}
      <p className="text-sm leading-relaxed text-foreground">
        {ask?.body ?? MEMBER_ACCOUNT_COPY.profileNotFinished}
      </p>
      {ask === null ? null : (
        <p className="text-sm leading-relaxed text-muted-foreground">{ask.reassurance}</p>
      )}
      <Button className="w-full" size="lg" asChild>
        <Link to={MEMBER_PROFILE_SETUP_PATH}>
          {ask?.action ?? MEMBER_ACCOUNT_COPY.profileNotFinishedAction}
        </Link>
      </Button>
    </section>
  );
}
